import React, { useState, useEffect, useCallback } from "react";
import { ClipLoader } from "react-spinners";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
} from "reactstrap";
import api from "../../../../../services/api";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../../../components/autopilot/styles";

// Renders inline markdown: **bold** and [text](url)
function renderInline(text) {
  const parts = [];
  const regex = /\*\*([^*]+)\*\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1] !== undefined) {
      parts.push(<strong key={match.index}>{match[1]}</strong>);
    } else {
      parts.push(
        <a key={match.index} href={match[3]} target="_blank" rel="noopener noreferrer" style={{ color: AUTOPILOT_COLORS.accent }}>
          {match[2]}
        </a>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function parseAnswerSections(text) {
  const lines = text.split("\n");
  const sections = [];
  let current = null;
  for (const line of lines) {
    const headingMatch = line.match(/^\*\*(.+?)\*\*\s*$/);
    if (headingMatch) {
      if (current) sections.push(current);
      current = { title: headingMatch[1], lines: [] };
    } else if (current) {
      if (line.trim() !== "---") current.lines.push(line);
    }
  }
  if (current) sections.push(current);
  return sections;
}

function SectionContent({ lines }) {
  const filtered = lines.filter(l => l.trim());
  return (
    <>
      {filtered.map((line, i) => {
        const trimmed = line.trim();
        if (trimmed.startsWith("•")) {
          return (
            <p key={i} style={{ margin: "0 0 0.35rem 0", lineHeight: "1.55" }}>
              {"• "}{renderInline(trimmed.slice(1).trim())}
            </p>
          );
        }
        return (
          <p key={i} style={{ margin: "0 0 0.4rem 0", lineHeight: "1.6" }}>
            {renderInline(trimmed)}
          </p>
        );
      })}
    </>
  );
}

function SubCard({ title, children, footer }) {
  return (
    <div
      style={{
        border: "1px solid " + AUTOPILOT_COLORS.border,
        borderRadius: "14px",
        padding: "16px 20px",
        marginBottom: "16px",
        background: AUTOPILOT_COLORS.insetSurface,
      }}
    >
      <p
        style={{
          fontSize: "0.8rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: AUTOPILOT_COLORS.accent,
          margin: "0 0 0.5rem 0",
          fontWeight: 700,
        }}
      >
        {title}
      </p>
      <div style={{ borderBottom: "1px solid " + AUTOPILOT_COLORS.border, marginBottom: "0.75rem" }} />
      {children}
      {footer && (
        <div style={{ marginTop: "0.6rem" }}>{footer}</div>
      )}
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `${mins} min atrás`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h atrás`;
  return `${Math.floor(hrs / 24)}d atrás`;
}

const INITIAL_VISIBLE = 5;

export default function RadarLogistico() {
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [items, setItems] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async (force = false) => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/dashboard/radar-logistico", {
        params: force ? { force: true } : {},
      });
      if (isMounted && res.status === 200) {
        const data = res.data;
        setAnswer(data.answer || null);
        setItems(Array.isArray(data.radar?.items) ? data.radar.items : []);
        setGeneratedAt(new Date().toISOString());
        setExpanded(false);
      }
    } catch (err) {
      if (isMounted) {
        setError("Não foi possível carregar o Radar Logístico.");
        console.error("RadarLogistico error:", err);
      }
    } finally {
      if (isMounted) setLoading(false);
    }
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visibleItems = expanded ? items : items.slice(0, INITIAL_VISIBLE);
  const hiddenCount = items.length - INITIAL_VISIBLE;

  return (
    <Card style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}>
      <CardHeader style={autopilotHeaderStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={autopilotEyebrowStyle}>Panorama logístico</div>
            <div style={{ ...autopilotTitleStyle, marginBottom: "0.3rem" }}>Radar logístico</div>
            {generatedAt && (
              <small style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.72rem" }}>
                Gerado {timeAgo(generatedAt)}
              </small>
            )}
          </div>
          <Button className="btn-sm" style={autopilotSecondaryButtonStyle} onClick={() => load(true)} disabled={loading}>
            &#8635; Atualizar
          </Button>
        </div>
      </CardHeader>

      <CardBody style={autopilotBodyStyle}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <ClipLoader sizeUnit="px" size={60} color="#1d8cf8" loading />
          </div>
        ) : error ? (
          <p style={{ color: "#f5365c" }}>{error}</p>
        ) : (
          <>
            {answer && parseAnswerSections(answer).map((section, i) => (
              <SubCard key={i} title={section.title}>
                <SectionContent lines={section.lines} />
              </SubCard>
            ))}

            {items.length > 0 && (
              <SubCard title="Notícias Recentes">
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {visibleItems.map((item, idx) => (
                    <li
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        padding: "0.5rem 0",
                        borderBottom: "1px solid rgba(43,53,83,0.9)",
                      }}
                    >
                      <span style={{ flex: 1, marginRight: "0.5rem" }}>
                        <span style={{ marginRight: "0.4rem", color: AUTOPILOT_COLORS.muted }}>•</span>
                        <strong style={{ color: AUTOPILOT_COLORS.text }}>{item.title}</strong>
                        <span style={{ color: AUTOPILOT_COLORS.muted, marginLeft: "0.4rem" }}>
                          — {item.sourceName}
                          {item.pubDate ? ` — ${timeAgo(item.pubDate)}` : ""}
                        </span>
                      </span>
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: AUTOPILOT_COLORS.accent, flexShrink: 0 }}
                          title="Abrir notícia"
                        >
                          ↗
                        </a>
                      )}
                    </li>
                  ))}
                </ul>

                {items.length > INITIAL_VISIBLE && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <Button
                      color="link"
                      size="sm"
                      style={{ padding: 0, color: AUTOPILOT_COLORS.accent }}
                      onClick={() => setExpanded(e => !e)}
                    >
                      {expanded
                        ? "▴ Mostrar menos"
                        : `+ ${hiddenCount} mais ▾`}
                    </Button>
                  </div>
                )}
              </SubCard>
            )}

            {!answer && items.length === 0 && (
              <p style={{ color: AUTOPILOT_COLORS.muted }}>
                Nenhuma informação disponível. Clique em Atualizar para tentar novamente.
              </p>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}
